import React, { useEffect, useState } from "react";
import { Box } from "rebass";
import { Type } from "../../base/Type/Type";
import { storage } from "../../utils/storage";
import { Modal } from "../../base/Modal/Modal";
import { Button } from "../../base/Button/Button";

type SetVisible = (visible: boolean) => void

enum NoticeStates {
  Dismissed = "dismissed",
  NotDismissed = "notDismissed"
}

type State = {
  state: NoticeStates
}

const getStorageName = () => "cookie-notice";

const setStorage = (state: NoticeStates, allowOptional?: boolean) => storage.set(getStorageName(), {
  state,
  allowOptional
});

const getStorage = () => {
  const rawValue = storage.get<State>(getStorageName());

  return rawValue?.state;
};
const useSetInitialVisibility = (setVisible: SetVisible) => {
  useEffect(() => {
    const value = getStorage();

    if (!value) {
      setStorage(NoticeStates.NotDismissed);
      setVisible(true);
    } else {
      setVisible(value === NoticeStates.NotDismissed);
    }
  }, []);
};

const closeNotice = (setVisible: SetVisible, allowOptional: boolean) => {
  setStorage(NoticeStates.Dismissed, allowOptional);
  setVisible(false);
};

function CookieNotice() {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [allowOptional, setAllowOptional] = useState(true);
  useSetInitialVisibility(setVisible);

  const closeModal = () => setModalOpen(false);
  const changeAllowOptional = () => setAllowOptional(previous => !previous);

  const submit = () => {
    closeModal();
    closeNotice(setVisible, allowOptional);
  };

  return visible &&
    <Box as="div"
         className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 px-10 sm:px-16 lg:px-32 h-40 sm:h-20 fixed bottom-0 bg-white w-full z-40">
      <Modal open={modalOpen} setOpen={setModalOpen}>
        <Box as="div" className="flex flex-col items-center justify-center p-8">
          <Box as="div" className="flex justify-end w-full">
            <button onClick={closeModal}>Close</button>
          </Box>
          <Box as="div" className="mt-3">
            <Type as="h2" className="font-foundersGrotesk text-xl mb-3">
              Privacy Overview
            </Type>
            <Type as="p" className="font-foundersGrotesk mb-5">
              This website uses cookies to improve your experience while you navigate through the website. Out of these,
              the cookies that are categorized as necessary are stored on your browser as they are essential for the
              working of basic functionalities of the website. We also use third-party cookies that help us analyze and
              understand how you use this website. These cookies will be stored in your browser only with your consent.
              You also have the option to opt-out of these cookies. But opting out of some of these cookies may affect
              your browsing experience.
            </Type>
            <div className="flex items-center mb-4">
              <input type="checkbox" checked={allowOptional} onChange={changeAllowOptional}
                     className="w-8 h-8 bg-gray-100 border-gray-300 rounded" />
              <label className="ml-2 font-medium text-ffBlack font-foundersGrotesk">Allow optional</label>
            </div>
          </Box>
          <Box as="div" className="w-full flex items-center justify-center">
            <Button type="button" variant="primary" onClick={submit} className="bg-ffBlack text-ffLightGrey">
              Save & accept
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box as="div" className="flex flex-col">
        <Type
          as="h3"
          className="font-foundersGrotesk text-ffBlack break-words text-md sm:text-lg"
        >
          This website uses cookies to ensure you get the best experience.
        </Type>
        <Type
          as="p"
          className="font-foundersGrotesk font-light text-ffBlack break-words text-xs sm:text-base mt-1 sm:mt-0"
        >
          Specifically, we use Google Analytics and respect Do-Not-Track as
          well as anonymizing IP addresses.
        </Type>
      </Box>
      <Box as="div" className="flex items-center justify-center mt-2 sm:mt-0 sm:h-full text-xs sm:text-base space-x-2">
        <Box as="div" role="button" className="underline text-ffBlack" onClick={() => setModalOpen(true)}>
          Cookie settings
        </Box>
        <Button variant="primary" type="button" className="bg-ffBlack text-ffLightGrey !py-2 !px-4 !text-base"
                onClick={() => closeNotice(setVisible, true)}>
          Accept
        </Button>
      </Box>
    </Box>;
}

export default CookieNotice;
